/**
 * Permissions Context
 *
 * Provides permission checking throughout the application.
 * Uses the user's permissions from AuthContext to determine access.
 */

import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import { useAuth } from './AuthContext';
import type { ModulePermissions } from '@business-assistant/shared/api/auth';

// =============================================================================
// TYPES
// =============================================================================

export type ModuleId = 'home' | 'projects' | 'calendar' | 'hub' | 'accounting' | 'settings';
export type PermissionLevel = 'none' | 'view' | 'edit' | 'admin';

export interface PermissionsContextType {
  /** Raw permissions object */
  permissions: ModulePermissions | null;
  /** User's job title */
  jobTitle: string | null;
  /** User's role (admin, user, member, visitor) */
  role: string | null;
  /** Check if user can access a module (view) */
  canAccess: (moduleId: ModuleId) => boolean;
  /** Check if user can edit in a module */
  canEdit: (moduleId: ModuleId) => boolean;
  /** Check if user has admin access to a module */
  canAdmin: (moduleId: ModuleId) => boolean;
  /** Get permission level for a module */
  getPermissionLevel: (moduleId: ModuleId) => PermissionLevel | boolean;
  /** Check if user is an admin */
  isAdmin: boolean;
  /** Check if user is a regular user or higher */
  isUser: boolean;
  /** Check if user is a member or higher */
  isMember: boolean;
  /** Check if user is a visitor */
  isVisitor: boolean;
}

// =============================================================================
// DEFAULTS
// =============================================================================

const DEFAULT_PERMISSIONS: ModulePermissions = {
  home: true,
  projects: 'none',
  calendar: 'none',
  hub: 'none',
  accounting: 'none',
  settings: false,
};

const ADMIN_PERMISSIONS: ModulePermissions = {
  home: true,
  projects: 'admin',
  calendar: 'edit',
  hub: 'edit',
  accounting: 'admin',
  settings: true,
};

// =============================================================================
// CONTEXT
// =============================================================================

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

// =============================================================================
// PROVIDER
// =============================================================================

interface PermissionsProviderProps {
  children: ReactNode;
}

export function PermissionsProvider({ children }: PermissionsProviderProps) {
  const { user, isAuthenticated } = useAuth();

  const value = useMemo<PermissionsContextType>(() => {
    // If not authenticated, return minimal permissions
    if (!isAuthenticated || !user) {
      return {
        permissions: null,
        jobTitle: null,
        role: null,
        canAccess: () => false,
        canEdit: () => false,
        canAdmin: () => false,
        getPermissionLevel: () => 'none',
        isAdmin: false,
        isUser: false,
        isMember: false,
        isVisitor: false,
      };
    }

    const role = user.role;
    const isAdmin = role === 'admin' || role === 'owner';
    const isUser = ['admin', 'user'].includes(role);
    const isMember = ['admin', 'user', 'member'].includes(role);
    const isVisitor = role === 'visitor';

    // Use admin permissions for admin role, otherwise use user's permissions or defaults
    const permissions: ModulePermissions = isAdmin
      ? ADMIN_PERMISSIONS
      : (user as any).permissions || DEFAULT_PERMISSIONS;

    const jobTitle = (user as any).jobTitle || null;

    /**
     * Get permission level for a module
     */
    const getPermissionLevel = (moduleId: ModuleId): PermissionLevel | boolean => {
      if (isAdmin) {
        // Admin has full access
        if (moduleId === 'home' || moduleId === 'settings') return true;
        return 'admin';
      }
      return permissions[moduleId];
    };

    /**
     * Check if user can access a module (view)
     */
    const canAccess = (moduleId: ModuleId): boolean => {
      if (isAdmin) return true;
      const level = permissions[moduleId];
      if (typeof level === 'boolean') return level;
      return level !== 'none';
    };

    /**
     * Check if user can edit in a module
     */
    const canEdit = (moduleId: ModuleId): boolean => {
      if (isAdmin) return true;
      const level = permissions[moduleId];
      if (typeof level === 'boolean') return level;
      return level === 'edit' || level === 'admin';
    };

    /**
     * Check if user has admin access to a module
     */
    const canAdmin = (moduleId: ModuleId): boolean => {
      if (isAdmin) return true;
      const level = permissions[moduleId];
      if (typeof level === 'boolean') return level && moduleId === 'settings';
      return level === 'admin';
    };

    return {
      permissions,
      jobTitle,
      role,
      canAccess,
      canEdit,
      canAdmin,
      getPermissionLevel,
      isAdmin,
      isUser,
      isMember,
      isVisitor,
    };
  }, [user, isAuthenticated]);

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

// =============================================================================
// HOOK
// =============================================================================

export function usePermissions(): PermissionsContextType {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
}

// =============================================================================
// MODULE GUARD COMPONENT
// =============================================================================

interface ModuleGuardProps {
  moduleId: ModuleId;
  requiredLevel?: 'access' | 'edit' | 'admin';
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * Component to guard content based on module permissions
 */
export function ModuleGuard({
  moduleId,
  requiredLevel = 'access',
  children,
  fallback = null,
}: ModuleGuardProps) {
  const { canAccess, canEdit, canAdmin } = usePermissions();

  let hasPermission = false;
  switch (requiredLevel) {
    case 'admin':
      hasPermission = canAdmin(moduleId);
      break;
    case 'edit':
      hasPermission = canEdit(moduleId);
      break;
    case 'access':
    default:
      hasPermission = canAccess(moduleId);
      break;
  }

  if (!hasPermission) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}

// =============================================================================
// ROLE GUARD COMPONENT
// =============================================================================

interface RoleGuardProps {
  allowedRoles: string[];
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * Component to guard content based on user role
 */
export function RoleGuard({
  allowedRoles,
  children,
  fallback = null,
}: RoleGuardProps) {
  const { role } = usePermissions();

  if (!role || !allowedRoles.includes(role)) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}
